import { forwardRef, useCallback, useId, useMemo, useRef, useState } from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import styled from '@mui/material/styles/styled';

import { PRIMARY_COLORS } from '../../../../themes/light';

export const MenuContainerV2 = forwardRef(
  (
    {
      children,
      disabled,
      menuLabel,
      onClickOutSide,
      onOpen,
      selectedLabel,
      popoverPositionX = 'center',
      withoutLabel = false,
      hidden,
      customHead,
      invisibleBackdrop = true,
    },
    ref,
  ) => {
    const headerRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const opened = Boolean(anchorEl);

    const open = useCallback(() => {
      if (disabled) return;
      onOpen?.();

      if (ref && 'current' in ref) setAnchorEl(ref.current);
      else setAnchorEl(headerRef.current);
    }, [ref, disabled, onOpen]);

    const close = useCallback(() => setAnchorEl(null), []);

    const handleClickOutSide = useCallback(() => {
      close();
      onClickOutSide?.();
    }, [close, onClickOutSide]);

    const headId = useId();
    const menuId = useId();

    const anchorOrigin = useMemo(
      () => ({
        vertical: 'bottom',
        horizontal: popoverPositionX,
      }),
      [popoverPositionX],
    );

    const transformOrigin = useMemo(
      () => ({
        vertical: 'top',
        horizontal: popoverPositionX,
      }),
      [popoverPositionX],
    );

    const passedRefWidth =
      ref && 'current' in ref && ref.current ? ref.current.offsetWidth : undefined;

    const headerRefWidth = headerRef.current?.offsetWidth;

    const slotProps = useMemo(
      () => ({ backdrop: { invisible: invisibleBackdrop } }),
      [invisibleBackdrop],
    );

    return (
      <>
        <Box
          display={hidden ? 'none' : 'flex'}
          alignItems="center"
          justifyContent="center"
          id={headId}
          aria-controls={opened ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={opened ? 'true' : undefined}
          gap={0.5}
          ref={headerRef}
          onClick={open}
          style={boxStyle}
          className="select"
          maxWidth="fit-content"
        >
          {customHead || (
            <>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={1}
                sx={{ '&:hover': { color: PRIMARY_COLORS.PRIMARY_GREEN } }}
              >
                {!withoutLabel && (
                  <Typography color="primary" fontWeight={600}>
                    {`${menuLabel}:`}
                  </Typography>
                )}

                {withoutLabel
                  ? selectedLabel && <Typography variant="h2">{selectedLabel}</Typography>
                  : selectedLabel && <span>{selectedLabel}</span>}
              </Box>
              <Box display="flex" justifyContent="center" alignItems="center">
                {opened ? (
                  <ExpandLessIcon
                    color="primary"
                    fontSize={selectedLabel && withoutLabel ? 'large' : 'medium'}
                  />
                ) : (
                  <ExpandMoreIcon
                    color="primary"
                    fontSize={selectedLabel && withoutLabel ? 'large' : 'medium'}
                  />
                )}
              </Box>
            </>
          )}
        </Box>
        {!disabled && !hidden && (
          <StyledPopover
            id={menuId}
            anchorEl={anchorEl}
            open={opened}
            onClose={handleClickOutSide}
            minWidth={passedRefWidth || headerRefWidth}
            aria-labelledby={headId}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            slotProps={slotProps}
          >
            {children({ close })}
          </StyledPopover>
        )}
      </>
    );
  },
);

MenuContainerV2.displayName = 'MenuContainerV2';

const StyledPopover = styled(Popover, {
  shouldForwardProp: (prop) => prop !== 'minWidth',
})(({ minWidth }) => ({
  '& .MuiPaper-root': {
    minWidth: `${minWidth}px` || 'auto',
    borderRadius: 0,
  },

  marginTop: 8,
  zIndex: 900,
}));

const boxStyle = {
  cursor: 'pointer',
};
